import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import {
  Sparkles, ArrowLeft, Copy, Check, Download, Trash2,
  ExternalLink, Image, Calendar, Monitor, Settings, X, CheckCircle,
  Archive, Layers, ChevronDown, ChevronUp, MapPin, FileText, Share2,
  BarChart3, Heart, Users, Mail, UserPlus, Film, Mic, MessageSquare,
  Globe, Lock, Eye, Instagram, Twitter, Facebook, MessageCircle, Linkedin, Send, Pencil, Radio, RefreshCw,
  CalendarDays, Printer
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { MoodIcon, VIBES, MOODS } from '../lib/moodIcons';
import { OCCASIONS } from '../lib/occasions';
import formatEventDate from '../lib/formatEventDate';
import { supportsCapability } from '../lib/capabilities';
import { useToast } from '../components/Toast';
import { EmptyState } from '../components/Skeleton';
import ReelEditor from '../components/ReelEditor';
import SiteHeader from '../components/SiteHeader';
import FooterExpanded from '../components/landing/FooterExpanded';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SHARE_PLATFORMS = [
  { id: 'instagram', name: 'Instagram',   Icon: Instagram,     color: '#E1306C' },
  { id: 'twitter',   name: 'X (Twitter)', Icon: Twitter,       color: '#1DA1F2' },
  { id: 'facebook',  name: 'Facebook',    Icon: Facebook,      color: '#4267B2' },
  { id: 'whatsapp',  name: 'WhatsApp',    Icon: MessageCircle, color: '#25D366' },
  { id: 'linkedin',  name: 'LinkedIn',    Icon: Linkedin,      color: '#0A66C2' },
  { id: 'telegram',  name: 'Telegram',    Icon: Send,          color: '#0088cc' },
  { id: 'native',    name: 'More Options',Icon: Share2,        color: '#6B7280' },
];

export default function AuraPage() {
  const { auraId } = useParams();
  const toast = useToast();
  const [aura, setAura] = useState(null);
  const [media, setMedia] = useState([]);
  const [pendingMedia, setPendingMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [editingMedia, setEditingMedia] = useState(null);  // host moderation working copy
  const [showSettings, setShowSettings] = useState(false);
  const [privacyMode, setPrivacyMode] = useState('open');
  // Editable event details (hydrated from the aura when it loads / modal opens)
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editMultiDay, setEditMultiDay] = useState(false);
  const [editEndDate, setEditEndDate] = useState('');
  const [editOccasion, setEditOccasion] = useState('');
  const [editVibe, setEditVibe] = useState('elegant');
  // Portal password: blank = keep the current one (it's stored hashed, so a
  // forgotten password can only be replaced, never shown).
  const [editPassword, setEditPassword] = useState('');
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [settingsError, setSettingsError] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);
  const [kioskPin, setKioskPin] = useState('');
  const [kioskBusy, setKioskBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [clusters, setClusters] = useState(null);
  const [viewMode, setViewMode] = useState('gallery'); // 'gallery', 'clusters', 'map', or 'analytics'
  const [expandedCluster, setExpandedCluster] = useState(null);
  const [mapData, setMapData] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [showCardModal, setShowCardModal] = useState(false);
  const [cardMessage, setCardMessage] = useState('Scan to share your moments');
  const [generatingCard, setGeneratingCard] = useState(false);
  const [cardHeadline, setCardHeadline] = useState('');
  const [cardSubline, setCardSubline] = useState('');
  const [cardPrimary, setCardPrimary] = useState('#1A1B2E');
  const [cardAccent, setCardAccent] = useState('#D4AF37');
  const [cardSize, setCardSize] = useState('a4');
  const [cardStyleName, setCardStyleName] = useState('minimal');
  const [cardUseCover, setCardUseCover] = useState(false);
  const [cardShowDate, setCardShowDate] = useState(true);
  const [cardShowVibe, setCardShowVibe] = useState(true);
  const [cardShowUrl, setCardShowUrl] = useState(true);
  const [cardShowInstructions, setCardShowInstructions] = useState(true);
  const [cardPassword, setCardPassword] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareMediaItem, setShareMediaItem] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmails, setInviteEmails] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [downloadingPostcards, setDownloadingPostcards] = useState(false);
  const [intakeEmail, setIntakeEmail] = useState('');
  const [intakeCopied, setIntakeCopied] = useState(false);
  const [intakeBusy, setIntakeBusy] = useState(false);
  const [collaborators, setCollaborators] = useState([]);
  const [newCoHostEmail, setNewCoHostEmail] = useState('');
  const [collaboratorError, setCollaboratorError] = useState('');
  const [collaboratorLoading, setCollaboratorLoading] = useState(false);
  const [showHighlightModal, setShowHighlightModal] = useState(false);
  const [showReelEditor, setShowReelEditor] = useState(false);
  const [musicTracks, setMusicTracks] = useState([]);
  const [selectedTrack, setSelectedTrack] = useState('elegant_piano');
  const [highlightReel, setHighlightReel] = useState(null);
  const [highlightError, setHighlightError] = useState('');
  // Sprint 36.3 — voice toast reel
  const [voiceReel, setVoiceReel] = useState(null);
  const [voiceReelError, setVoiceReelError] = useState('');
  // Closing Ceremony — auto-generated reel + photo book sent at expiry.
  const [ceremonyBusy, setCeremonyBusy] = useState(false);
  const [ceremonyMessage, setCeremonyMessage] = useState('');
  // View-only live share link (/live/:token) for people not at the event.
  const [showLiveModal, setShowLiveModal] = useState(false);
  const [liveShare, setLiveShare] = useState(null); // { enabled, url } | null while loading
  const [liveBusy, setLiveBusy] = useState(false);
  const [liveCopied, setLiveCopied] = useState(false);

  const openLiveShare = async () => {
    setShowLiveModal(true);
    setLiveShare(null);
    setLiveCopied(false);
    try {
      const { data } = await axios.get(`${API_URL}/api/auras/${auraId}/live-share`, { withCredentials: true });
      setLiveShare(data);
    } catch (e) {
      setShowLiveModal(false);
      toast.error(e.response?.data?.detail || 'Could not load the live view link');
    }
  };

  const createLiveShare = async () => {
    if (liveShare?.enabled && !window.confirm('Create a new link? The current link will stop working for everyone who has it.')) return;
    setLiveBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/auras/${auraId}/live-share`, {}, { withCredentials: true });
      const regenerated = liveShare?.enabled;
      setLiveShare(data);
      setLiveCopied(false);
      toast.success(regenerated ? 'New link created — the old one no longer works' : 'Live view link is on');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create the link');
    } finally {
      setLiveBusy(false);
    }
  };

  const disableLiveShare = async () => {
    if (!window.confirm('Turn off the live view link? Anyone watching will be disconnected.')) return;
    setLiveBusy(true);
    try {
      const { data } = await axios.delete(`${API_URL}/api/auras/${auraId}/live-share`, { withCredentials: true });
      setLiveShare(data);
      toast.success('Live view link turned off');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not turn off the link');
    } finally {
      setLiveBusy(false);
    }
  };

  const copyLiveShare = async () => {
    try {
      await navigator.clipboard.writeText(liveShare.url);
      setLiveCopied(true);
      setTimeout(() => setLiveCopied(false), 2000);
    } catch {
      toast.error('Copy failed — select the link and copy it manually');
    }
  };

  const fetchAuraAndMedia = useCallback(async () => {
    try {
      const [auraRes, mediaRes, pendingRes] = await Promise.all([
        axios.get(`${API_URL}/api/auras/${auraId}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/media/${auraId}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/media/${auraId}?include_pending=true`, { withCredentials: true })
      ]);
      setAura(auraRes.data);
      setMedia(mediaRes.data.filter(m => m.is_approved));
      setPendingMedia(pendingRes.data.filter(m => !m.is_approved));
      setPrivacyMode(auraRes.data.privacy_mode);
    } catch (error) {
      console.error('Error fetching aura:', error);
    } finally {
      setLoading(false);
    }
  }, [auraId]);

  useEffect(() => {
    fetchAuraAndMedia();
  }, [fetchAuraAndMedia]);

  // "Order Prints" only shows once printing is switched on server-side.
  const [printEnabled, setPrintEnabled] = useState(false);
  useEffect(() => {
    axios.get(`${API_URL}/api/print/catalog`)
      .then(({ data }) => setPrintEnabled(!!data.enabled))
      .catch(() => setPrintEnabled(false));
  }, []);

  // Closing-ceremony email links host here with ?vault=upsell — kick off the
  // existing vault-subscription Stripe checkout flow automatically.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('vault') === 'upsell' && auraId) {
        (async () => {
          try {
            const { data } = await axios.post(
              `${API_URL}/api/auras/${auraId}/vault-subscription/checkout`, {},
              { withCredentials: true }
            );
            if (data?.checkout_url) {
              window.location.href = data.checkout_url;
            }
          } catch (err) {
            console.warn('Vault upsell checkout failed', err?.response?.data?.detail || err);
          }
        })();
      }
    } catch { /* ignore */ }
  }, [auraId]);

  const handleCopyLink = () => {
    const portalUrl = `${window.location.origin}/portal/${aura.aura_key}`;
    navigator.clipboard.writeText(portalUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDeleteMedia = async (mediaId) => {
    try {
      await axios.delete(`${API_URL}/api/media/${mediaId}`, { withCredentials: true });
      setMedia(media.filter(m => m.id !== mediaId));
      setPendingMedia(pendingMedia.filter(m => m.id !== mediaId));
      setSelectedMedia(null);
    } catch (error) {
      console.error('Error deleting media:', error);
    }
  };

  const saveMediaEdit = async () => {
    try {
      const { id, caption, mood, guest_name } = editingMedia;
      const { data } = await axios.put(`${API_URL}/api/media/${id}`,
        { caption, mood, guest_name }, { withCredentials: true });
      const patch = m => (m.id === id ? { ...m, caption: data.caption, mood: data.mood, guest_name: data.guest_name } : m);
      setMedia(ms => ms.map(patch));
      setPendingMedia(ms => ms.map(patch));
      setSelectedMedia(sm => (sm && sm.id === id ? { ...sm, ...data } : sm));
      setEditingMedia(null);
    } catch (error) {
      console.error('Error saving media:', error);
      toast.error(error.response?.data?.detail || 'Failed to save changes');
    }
  };

  const handleApproveMedia = async (mediaId) => {
    try {
      await axios.post(`${API_URL}/api/media/${mediaId}/approve`, {}, { withCredentials: true });
      const approved = pendingMedia.find(m => m.id === mediaId);
      if (approved) {
        approved.is_approved = true;
        setMedia([approved, ...media]);
        setPendingMedia(pendingMedia.filter(m => m.id !== mediaId));
      }
    } catch (error) {
      console.error('Error approving media:', error);
    }
  };

  const saveKioskPin = async (clear) => {
    if (!clear && !/^\d{4}$/.test(kioskPin)) {
      toast.error('PIN must be 4 digits');
      return;
    }
    setKioskBusy(true);
    try {
      await axios.put(`${API_URL}/api/auras/${auraId}/kiosk-pin`,
        { pin: clear ? null : kioskPin }, { withCredentials: true });
      setKioskPin('');
      toast.success(clear ? 'Kiosk PIN removed' : 'Kiosk PIN set');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update kiosk PIN');
    } finally {
      setKioskBusy(false);
    }
  };

  const kioskUrl = aura ? `${window.location.origin}/portal/${aura.aura_key}/kiosk` : '';

  const hydrateEditFields = (a) => {
    setEditTitle(a.title || '');
    setEditDescription(a.description || '');
    setEditDate(a.event_date || '');
    setEditMultiDay(!!a.end_date);
    setEditEndDate(a.end_date || '');
    setEditOccasion(a.occasion || '');
    setEditVibe(a.vibe || 'elegant');
    setEditPassword('');
    setShowEditPassword(false);
  };

  const handleUpdateSettings = async () => {
    setSettingsError('');
    if (!editTitle.trim()) {
      setSettingsError('Event name is required');
      return;
    }
    if (editMultiDay && editEndDate && editEndDate < editDate) {
      setSettingsError('End date must be on or after the start date');
      return;
    }
    const newPassword = privacyMode === 'locked' ? editPassword.trim() : '';
    if (privacyMode === 'locked' && !aura?.has_password && !newPassword) {
      setSettingsError('Set a password to lock this aura');
      return;
    }
    if (newPassword && newPassword.length < 4) {
      setSettingsError('Portal password must be at least 4 characters');
      return;
    }
    setSavingSettings(true);
    try {
      await axios.put(
        `${API_URL}/api/auras/${auraId}`,
        {
          title: editTitle,
          description: editDescription,
          event_date: editDate,
          end_date: editMultiDay && editEndDate ? editEndDate : null,
          occasion: editOccasion || null,
          vibe: editVibe,
          privacy_mode: privacyMode,
          ...(newPassword ? { password: newPassword } : {}),
        },
        { withCredentials: true }
      );
      if (newPassword) {
        toast.success('Portal password updated — guests will need the new one');
      }
      // Refetch so the header, date range, occasion and capability-gated
      // buttons (seating / RSVP) all reflect the new values.
      await fetchAuraAndMedia();
      setShowSettings(false);
    } catch (error) {
      setSettingsError(error.response?.data?.detail || 'Failed to save changes');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleDownloadZip = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/auras/${auraId}/download`,
        { 
          withCredentials: true,
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${aura.title.replace(/[^a-z0-9]/gi, '_')}_moments.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading ZIP:', error);
      alert('Failed to download. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadReport = async () => {
    setDownloadingReport(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/auras/${auraId}/report`, {},
        { withCredentials: true, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${aura.title.replace(/[^a-z0-9]/gi, '_')}_report.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (error.response?.status === 403) {
        alert('Upgrade to Pro to download analytics reports.');
      } else {
        alert('Failed to generate report. Please try again.');
      }
    } finally {
      setDownloadingReport(false);
    }
  };

  const handleDownloadPostcards = async () => {
    setDownloadingPostcards(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/auras/${auraId}/postcards.pdf`,
        { withCredentials: true, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${aura.title.replace(/[^a-z0-9]/gi, '_')}_postcards.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (error.response?.status === 400) {
        alert('No images available yet — upload some photos first.');
      } else {
        alert('Failed to generate postcards. Please try again.');
      }
    } finally {
      setDownloadingPostcards(false);
    }
  };

  const fetchIntakeEmail = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/auras/${auraId}/intake-email`, { withCredentials: true });
      setIntakeEmail(r.data.intake_email || '');
    } catch (e) { /* host-only; ignore for non-hosts */ }
  }, [auraId]);

  const handleCopyIntake = async () => {
    if (!intakeEmail) return;
    try {
      await navigator.clipboard.writeText(intakeEmail);
      setIntakeCopied(true);
      setTimeout(() => setIntakeCopied(false), 1500);
    } catch (e) { /* ignore */ }
  };

  const handleRotateIntake = async () => {
    if (!window.confirm('Regenerate the intake address? The old address will stop working immediately.')) return;
    setIntakeBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/auras/${auraId}/intake-email/rotate`, {}, { withCredentials: true });
      setIntakeEmail(r.data.intake_email || '');
    } catch (e) {
      alert('Failed to rotate intake address.');
    } finally {
      setIntakeBusy(false);
    }
  };

  useEffect(() => { fetchIntakeEmail(); }, [fetchIntakeEmail]);
  // Seed card colors from the aura's theme whenever the card dialog opens.
  useEffect(() => {
    if (showCardModal && aura) {
      setCardPrimary(aura.primary_color || '#1A1B2E');
      setCardAccent(aura.accent_color || '#D4AF37');
    }
  }, [showCardModal, aura]);

  const openHighlightModal = async () => {
    setShowHighlightModal(true);
    setHighlightError('');
    if (musicTracks.length === 0) {
      try {
        const { data } = await axios.get(`${API_URL}/api/highlight/music`);
        setMusicTracks(data || []);
      } catch {
        setMusicTracks([]);
      }
    }
  };

  const handleGenerateHighlight = async () => {
    setHighlightError('');
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auras/${auraId}/highlight`,
        { music_track: selectedTrack },
        { withCredentials: true }
      );
      setHighlightReel(data);
      // Poll status every 4 seconds until ready/failed
      const interval = setInterval(async () => {
        try {
          const { data: status } = await axios.get(
            `${API_URL}/api/auras/${auraId}/highlight/${data.id}`,
            { withCredentials: true }
          );
          setHighlightReel(status);
          if (status.status === 'ready' || status.status === 'failed') {
            clearInterval(interval);
          }
        } catch {
          clearInterval(interval);
        }
      }, 4000);
    } catch (error) {
      setHighlightError(error.response?.data?.detail || 'Failed to start highlight reel.');
    }
  };

  const handleGenerateVoiceReel = async () => {
    setVoiceReelError('');
    setVoiceReel({ status: 'queued' });
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auras/${auraId}/voice-toasts.mp4`, {},
        { withCredentials: true }
      );
      const taskId = data.task_id;
      const interval = setInterval(async () => {
        try {
          const { data: st } = await axios.get(
            `${API_URL}/api/auras/${auraId}/voice-toasts.mp4/status/${taskId}`,
            { withCredentials: true }
          );
          setVoiceReel(st);
          if (st.status === 'ready' || st.status === 'failed') clearInterval(interval);
        } catch { clearInterval(interval); }
      }, 4000);
    } catch (err) {
      setVoiceReelError(err.response?.data?.detail || 'Failed to start voice toast reel');
      setVoiceReel(null);
    }
  };

  const handleTriggerClosingCeremony = async () => {
    setCeremonyMessage('');
    setCeremonyBusy(true);
    try {
      await axios.post(
        `${API_URL}/api/auras/${auraId}/closing-ceremony`, {},
        { withCredentials: true }
      );
      setCeremonyMessage('Generating your ceremony... refresh in a minute.');
    } catch (err) {
      setCeremonyMessage(err.response?.data?.detail || 'Failed to start ceremony.');
    } finally {
      setCeremonyBusy(false);
    }
  };

  const handleRefreshCeremony = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auras/${auraId}`, { withCredentials: true });
      setAura(data);
      setCeremonyMessage('');
    } catch { /* ignore */ }
  };

  const handleSendInvites = async () => {
    const emails = inviteEmails
      .split(/[\n,;]+/)
      .map(e => e.trim())
      .filter(e => e.length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
    if (!emails.length) {
      setInviteResult({ error: 'Please enter at least one valid email address.' });
      return;
    }
    if (emails.length > 50) {
      setInviteResult({ error: 'Maximum 50 emails per request.' });
      return;
    }
    setInviteSending(true);
    setInviteResult(null);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auras/${auraId}/invite`,
        { emails, message: inviteMessage },
        { withCredentials: true }
      );
      setInviteResult(data);
      setInviteEmails('');
      setInviteMessage('');
    } catch (error) {
      setInviteResult({ error: error.response?.data?.detail || 'Failed to send invites' });
    } finally {
      setInviteSending(false);
    }
  };

  const fetchCollaborators = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auras/${auraId}/collaborators`, { withCredentials: true });
      setCollaborators(data);
    } catch {
      // non-host users won't have access — silently ignore
    }
  };

  const handleAddCoHost = async () => {
    if (!newCoHostEmail.trim()) return;
    setCollaboratorLoading(true);
    setCollaboratorError('');
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auras/${auraId}/collaborators`,
        { email: newCoHostEmail.trim() },
        { withCredentials: true }
      );
      setCollaborators(prev => [...prev, data]);
      setNewCoHostEmail('');
    } catch (error) {
      setCollaboratorError(error.response?.data?.detail || 'Failed to add co-host');
    } finally {
      setCollaboratorLoading(false);
    }
  };

  const handleRemoveCoHost = async (collabId) => {
    try {
      await axios.delete(`${API_URL}/api/auras/${auraId}/collaborators/${collabId}`, { withCredentials: true });
      setCollaborators(prev => prev.filter(c => c.id !== collabId));
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to remove co-host');
    }
  };

  const fetchClusters = async () => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/auras/${auraId}/clusters`,
        { withCredentials: true }
      );
      setClusters(data);
    } catch (error) {
      console.error('Error fetching clusters:', error);
      setClusters({ clusters: [] });
      toast.error('Could not load people groups');
    }
  };

  // Photo privacy toggles (host only) save straight away.
  const [privacyBusy, setPrivacyBusy] = useState(false);
  const savePrivacyToggle = async (field, value) => {
    setPrivacyBusy(true);
    try {
      await axios.put(`${API_URL}/api/auras/${auraId}`, { [field]: value }, { withCredentials: true });
      setAura(prev => (prev ? { ...prev, [field]: value } : prev));
      if (field === 'show_photo_map') setMapData(null);
      toast.success(field === 'show_photo_map'
        ? (value ? 'Photo map on — only you can see it' : 'Photo map off')
        : (value ? 'Find me is on — the guest portal now says so' : 'Find me is off and all guest results links were deleted'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not save');
    } finally {
      setPrivacyBusy(false);
    }
  };

  const fetchMapData = async () => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/auras/${auraId}/map`,
        { withCredentials: true }
      );
      setMapData(data);
    } catch (error) {
      console.error('Error fetching map data:', error);
      setMapData({ locations: [] });
      toast.error('Could not load the map');
    }
  };

  const fetchAnalytics = async () => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/auras/${auraId}/analytics`,
        { withCredentials: true }
      );
      setAnalyticsData(data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Could not load analytics');
    }
  };

  const toggleViewMode = (mode) => {
    setViewMode(mode);
    if (mode === 'clusters' && !clusters) {
      fetchClusters();
    } else if (mode === 'map' && !mapData && aura?.show_photo_map) {
      fetchMapData();
    } else if (mode === 'analytics' && !analyticsData) {
      fetchAnalytics();
    }
  };

  const handleGenerateCard = async () => {
    setGeneratingCard(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/auras/${auraId}/card`,
        {
          custom_message: cardMessage,
          headline: cardHeadline || null,
          subline: cardSubline || null,
          primary_color: cardPrimary,
          accent_color: cardAccent,
          size: cardSize,
          style: cardStyleName,
          use_cover: cardUseCover,
          show_date: cardShowDate,
          show_vibe: cardShowVibe,
          show_url: cardShowUrl,
          show_instructions: cardShowInstructions,
          card_password: cardPassword || null,
        },
        { 
          withCredentials: true,
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${aura.title.replace(/[^a-z0-9]/gi, '_')}_aura_card.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setShowCardModal(false);
    } catch (error) {
      console.error('Error generating card:', error);
      alert('Failed to generate card. Please try again.');
    } finally {
      setGeneratingCard(false);
    }
  };

  const handleShare = (platform, mediaItem = null) => {
    const portalUrl = `${window.location.origin}/portal/${aura.aura_key}`;
    const shareText = mediaItem
      ? `Check out this moment from ${aura.title}!`
      : `Join ${aura.title} and share your moments!`;

    const shareUrls = {
      instagram: () => {
        // Instagram doesn't support direct URL sharing, copy to clipboard instead
        navigator.clipboard.writeText(`${shareText}\n${portalUrl}`);
        alert('Link copied! Open Instagram and paste in your story.');
      },
      twitter: () => {
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(portalUrl)}`, '_blank');
      },
      facebook: () => {
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(portalUrl)}&quote=${encodeURIComponent(shareText)}`, '_blank');
      },
      whatsapp: () => {
        window.open(`https://wa.me/?text=${encodeURIComponent(`${shareText}\n${portalUrl}`)}`, '_blank');
      },
      linkedin: () => {
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(portalUrl)}`, '_blank');
      },
      telegram: () => {
        window.open(`https://t.me/share/url?url=${encodeURIComponent(portalUrl)}&text=${encodeURIComponent(shareText)}`, '_blank');
      },
      native: async () => {
        if (navigator.share) {
          try {
            await navigator.share({
              title: aura.title,
              text: shareText,
              url: portalUrl
            });
          } catch (err) {
            if (err.name !== 'AbortError') {
              console.error('Error sharing:', err);
            }
          }
        } else {
          navigator.clipboard.writeText(`${shareText}\n${portalUrl}`);
          alert('Link copied to clipboard!');
        }
      }
    };

    shareUrls[platform]?.();
    setShowShareModal(false);
    setShareMediaItem(null);
  };

  const formatDate = () => formatEventDate(aura);

  if (loading) {
    return (
      <div className="min-h-screen bg-midnight flex items-center justify-center">
        <div className="text-champagne animate-pulse font-serif text-xl">Loading aura...</div>
      </div>
    );
  }

  if (!aura) {
    return (
      <div className="min-h-screen bg-midnight flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-serif font-medium text-text-light mb-2">Aura not found</h2>
          <Link to="/dashboard" className="text-champagne hover:underline">
            Return to dashboard
          </Link>
        </div>
      </div>
    );
  }

  const portalUrl = `${window.location.origin}/portal/${aura.aura_key}`;

  return (
    <div className="min-h-screen bg-midnight">
      <SiteHeader />
      <div className="border-b border-champagne/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 flex items-center justify-end">
          <Link
            to="/dashboard"
            className="flex items-center gap-2 text-text-muted hover:text-champagne transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Dashboard</span>
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Aura Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid lg:grid-cols-3 gap-8 mb-12"
        >
          {/* Aura Info */}
          <div className="lg:col-span-2">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h1 className="text-3xl sm:text-4xl font-serif font-medium text-text-light mb-2">
                  {aura.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-text-muted">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {formatDate()}
                  </span>
                  <span className="flex items-center gap-1">
                    <Image className="w-4 h-4" />
                    {media.length} moments
                  </span>
                  <span className="mood-badge">
                    {aura.vibe}
                  </span>
                </div>
              </div>
              <button
                onClick={() => { hydrateEditFields(aura); setSettingsError(''); setShowSettings(true); fetchCollaborators(); }}
                className="p-2 text-text-muted hover:text-champagne hover:bg-champagne/10 rounded-lg transition-colors"
                data-testid="aura-settings-btn"
              >
                <Settings className="w-5 h-5" />
              </button>
            </div>
            {aura.description && (
              <p className="text-text-muted mb-6">{aura.description}</p>
            )}

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleCopyLink}
                className="btn-outline flex items-center gap-2"
                data-testid="copy-link-btn"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy Portal Link'}
              </button>
              <a
                href={portalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline flex items-center gap-2"
                data-testid="preview-link-btn"
              >
                <ExternalLink className="w-4 h-4" />
                Preview Portal
              </a>
              <Link
                to={`/auracast/${aura.aura_key}`}
                className="btn-outline flex items-center gap-2"
              >
                <Monitor className="w-4 h-4" />
                AuraCast Live
              </Link>
              <button
                onClick={openLiveShare}
                className="btn-outline flex items-center gap-2"
                data-testid="share-live-view-btn"
              >
                <Radio className="w-4 h-4" />
                Share Live View
              </button>
              <button
                onClick={() => setShowCardModal(true)}
                className="btn-outline flex items-center gap-2"
                data-testid="print-card-btn"
              >
                <FileText className="w-4 h-4" />
                Print Aura Card
              </button>
              <button
                onClick={() => { setShowInviteModal(true); setInviteResult(null); }}
                className="btn-outline flex items-center gap-2"
                data-testid="invite-guests-btn"
              >
                <Mail className="w-4 h-4" />
                Invite Guests
              </button>
              <Link
                to={`/aura/${auraId}/site`}
                className="btn-outline flex items-center gap-2"
                data-testid="event-site-btn"
              >
                <ExternalLink className="w-4 h-4" />
                {supportsCapability(aura, 'rsvp') ? 'Event Site & RSVP' : 'Event Site'}
              </Link>
              <Link
                to={`/aura/${auraId}/photo-book`}
                className="btn-outline flex items-center gap-2"
                data-testid="photo-book-btn"
              >
                <FileText className="w-4 h-4" />
                Photo Book
              </Link>
              <Link
                to={`/aura/${auraId}/calendar`}
                className="btn-outline flex items-center gap-2"
                data-testid="calendar-btn"
              >
                <CalendarDays className="w-4 h-4" />
                Calendar
              </Link>
              {printEnabled && (
                <Link
                  to={`/aura/${auraId}/print`}
                  className="btn-outline flex items-center gap-2"
                  data-testid="order-prints-btn"
                >
                  <Printer className="w-4 h-4" />
                  Order Prints
                </Link>
              )}
              {supportsCapability(aura, 'seating') && (
                <Link
                  to={`/aura/${auraId}/seating`}
                  className="btn-outline flex items-center gap-2"
                  data-testid="seating-chart-btn"
                >
                  <Users className="w-4 h-4" />
                  Seating chart
                </Link>
              )}
              {media.filter(m => m.file_type === 'image').length >= 3 && (
                <button
                  onClick={() => setShowReelEditor(true)}
                  className="btn-outline flex items-center gap-2"
                  data-testid="make-reel-btn"
                >
                  <Film className="w-4 h-4" />
                  Make a Reel
                </button>
              )}
              <button
                onClick={() => setShowShareModal(true)}
                className="btn-outline flex items-center gap-2"
                data-testid="share-btn"
              >
                <Share2 className="w-4 h-4" />
                Share
              </button>
              {media.length > 0 && (
                <button
                  onClick={handleDownloadZip}
                  disabled={downloading}
                  className="btn-champagne flex items-center gap-2 disabled:opacity-50"
                  data-testid="download-all-btn"
                >
                  <Archive className="w-4 h-4" />
                  {downloading ? 'Preparing ZIP...' : 'Download All (ZIP)'}
                </button>
              )}
            </div>
          </div>

          {/* QR Code Card */}
          <div className="aura-card rounded-xl p-6 text-center">
            <h3 className="text-sm font-bold uppercase tracking-widest text-champagne mb-4">
              The Aura Key
            </h3>
            <div className="bg-white p-4 rounded-xl inline-block aura-glow mb-4">
              <QRCodeSVG
                value={portalUrl}
                size={160}
                fgColor="#1A1B2E"
                level="M"
              />
            </div>
            <p className="text-sm text-text-muted mb-3">
              Share this key with your guests
            </p>
            <button
              onClick={() => setShowCardModal(true)}
              className="text-champagne text-sm hover:underline"
            >
              Download printable card →
            </button>
          </div>

          {/* Email-to-Aura — guests can email photos straight into the gallery */}
          {aura?.is_host && intakeEmail && (
            <div className="aura-card rounded-xl p-5" data-testid="intake-email-section">
              <div className="flex items-center gap-2 text-text-light font-semibold mb-1">
                <Mail className="w-4 h-4 text-champagne" /> Email photos in
              </div>
              <p className="text-text-muted text-xs mb-3">
                Share this address with guests — anything they email (with photo/video attached) lands in this Aura. No app needed.
              </p>
              <code className="block bg-midnight/40 text-champagne px-3 py-2 rounded text-xs break-all mb-3" data-testid="intake-email-value">
                {intakeEmail}
              </code>
              <div className="flex flex-wrap gap-2">
                <button onClick={handleCopyIntake} className="btn-outline flex items-center gap-2 text-xs" data-testid="intake-email-copy">
                  {intakeCopied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  {intakeCopied ? 'Copied' : 'Copy address'}
                </button>
                <button onClick={handleRotateIntake} disabled={intakeBusy} className="btn-outline flex items-center gap-2 text-xs" data-testid="intake-email-rotate">
                  {intakeBusy ? 'Rotating…' : 'Regenerate'}
                </button>
              </div>
            </div>
          )}
        </motion.div>

        {/* Closing Ceremony — host-only saudade send-off */}
        {aura?.is_host && (() => {
          const completed = !!aura.closing_ceremony_completed_at;
          const expiresAt = aura.expires_at ? new Date(aura.expires_at) : null;
          const now = new Date();
          const daysUntilExpiry = expiresAt ? Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24)) : null;
          const showTriggerCTA = !completed && daysUntilExpiry !== null && daysUntilExpiry <= 7;
          const hidden = !completed && (daysUntilExpiry === null || daysUntilExpiry > 7);
          if (hidden) return null;
          return (
            <div className="aura-card rounded-xl p-5 sm:p-6 mb-10 border border-champagne/30" data-testid="closing-ceremony-section">
              <div className="flex items-center gap-2 mb-3">
                <Film className="w-5 h-5 text-champagne" />
                <h2 className="text-lg sm:text-xl font-serif font-medium text-text-light">Closing Ceremony</h2>
              </div>
              {completed ? (
                <>
                  <p className="text-text-muted text-sm mb-4">
                    Your aura's closing ceremony is ready. Download the highlight reel and the printable photo book below — and preserve it all in the Vault before the saudade takes the rest.
                  </p>
                  <div className="flex flex-wrap gap-3 mb-4">
                    {aura.closing_ceremony_reel_url && (
                      <a
                        href={`${API_URL}${aura.closing_ceremony_reel_url}`}
                        download
                        className="btn-outline flex items-center gap-2 text-sm"
                        data-testid="closing-reel-btn"
                      >
                        <Film className="w-4 h-4" /> Download Reel
                      </a>
                    )}
                    {aura.closing_ceremony_book_url && (
                      <a
                        href={`${API_URL}${aura.closing_ceremony_book_url}`}
                        download
                        className="btn-outline flex items-center gap-2 text-sm"
                        data-testid="closing-book-btn"
                      >
                        <FileText className="w-4 h-4" /> Download Photo Book
                      </a>
                    )}
                  </div>
                  {!aura.is_vaulted && (
                    <div className="bg-gradient-to-br from-aura-purple/20 to-aura-gold/20 rounded-lg p-4 border border-champagne/40">
                      <p className="text-text-light text-sm mb-3">
                        Preserve every memory in full resolution, forever — $29/year.
                      </p>
                      <Link
                        to={`/aura/${auraId}?vault=upsell`}
                        className="btn-champagne inline-flex items-center gap-2 text-sm"
                        data-testid="closing-vault-cta"
                      >
                        Preserve in Vault
                      </Link>
                    </div>
                  )}
                </>
              ) : showTriggerCTA ? (
                <>
                  <p className="text-text-muted text-sm mb-4">
                    {daysUntilExpiry <= 0
                      ? "Your aura has reached its quiet end. Generate the closing ceremony now."
                      : `Your aura ends in ${daysUntilExpiry} day${daysUntilExpiry === 1 ? '' : 's'}. You can generate the closing ceremony at any time.`}
                  </p>
                  <button
                    onClick={handleTriggerClosingCeremony}
                    disabled={ceremonyBusy}
                    className="btn-champagne inline-flex items-center gap-2 text-sm disabled:opacity-50"
                    data-testid="closing-generate-btn"
                  >
                    <Sparkles className="w-4 h-4" />
                    {ceremonyBusy ? 'Generating your ceremony…' : 'Generate closing ceremony now'}
                  </button>
                  {ceremonyMessage && (
                    <div className="mt-3 flex items-center gap-3">
                      <p className="text-champagne text-sm">{ceremonyMessage}</p>
                      <button
                        onClick={handleRefreshCeremony}
                        className="text-xs text-text-muted hover:text-champagne underline"
                      >
                        Refresh
                      </button>
                    </div>
                  )}
                </>
              ) : null}
            </div>
          );
        })()}

        {/* Pending Approval (for moderated auras) */}
        {aura.privacy_mode === 'moderated' && pendingMedia.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-serif font-medium text-champagne mb-4 flex items-center gap-2">
              <span className="w-2 h-2 bg-champagne rounded-full animate-pulse" />
              Pending Approval ({pendingMedia.length})
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {pendingMedia.map((item) => (
                <div
                  key={item.id}
                  className="relative group rounded-xl overflow-hidden border border-champagne/30"
                >
                  {item.file_type === 'video' ? (
                    <video
                      src={`${API_URL}${item.url}`}
                      className="w-full aspect-square object-cover"
                    />
                  ) : (
                    <img
                      src={`${API_URL}${item.url}`}
                      alt=""
                      className="w-full aspect-square object-cover"
                    />
                  )}
                  <div className="absolute inset-0 bg-midnight/80 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button
                      onClick={() => handleApproveMedia(item.id)}
                      className="w-10 h-10 bg-success rounded-full flex items-center justify-center hover:scale-110 transition-transform"
                      title="Approve" aria-label="Approve"
                    >
                      <CheckCircle className="w-5 h-5 text-white" />
                    </button>
                    <button
                      onClick={() => handleDeleteMedia(item.id)}
                      className="w-10 h-10 bg-error rounded-full flex items-center justify-center hover:scale-110 transition-transform"
                      title="Delete" aria-label="Delete"
                    >
                      <Trash2 className="w-5 h-5 text-white" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Media Gallery - Masonry Style */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-serif font-medium text-text-light">
              Living Gallery
            </h2>
            <div className="flex gap-2">
              {media.length >= 3 && (
                <>
                  <button
                    onClick={() => toggleViewMode('gallery')}
                    className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1 transition-colors ${
                      viewMode === 'gallery' ? 'bg-champagne text-midnight' : 'text-text-muted hover:text-champagne'
                    }`}
                  >
                    <Image className="w-4 h-4" />
                    Gallery
                  </button>
                  <button
                    onClick={() => toggleViewMode('clusters')}
                    className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1 transition-colors ${
                      viewMode === 'clusters' ? 'bg-champagne text-midnight' : 'text-text-muted hover:text-champagne'
                    }`}
                  >
                    <Layers className="w-4 h-4" />
                    Clusters
                  </button>
                  <button
                    onClick={() => toggleViewMode('map')}
                    className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1 transition-colors ${
                      viewMode === 'map' ? 'bg-champagne text-midnight' : 'text-text-muted hover:text-champagne'
                    }`}
                    data-testid="map-view-btn"
                  >
                    <MapPin className="w-4 h-4" />
                    Map
                  </button>
                  {aura?.is_host && (
                    <button
                      onClick={() => toggleViewMode('analytics')}
                      className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1 transition-colors ${
                        viewMode === 'analytics' ? 'bg-champagne text-midnight' : 'text-text-muted hover:text-champagne'
                      }`}
                      data-testid="analytics-view-btn"
                    >
                      <BarChart3 className="w-4 h-4" />
                      Analytics
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          {media.length === 0 ? (
            <div className="text-center py-16 aura-card rounded-xl">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-aura-purple/20 to-aura-gold/20 flex items-center justify-center mx-auto mb-4">
                <Image className="w-8 h-8 text-champagne" />
              </div>
              <h3 className="text-xl font-serif font-medium text-text-light mb-2">No moments yet</h3>
              <p className="text-text-muted">
                Share the portal link or QR code to start collecting memories
              </p>
            </div>
          ) : viewMode === 'map' ? (
            /* Map View */
            <div className="aura-card rounded-xl p-6">
              <div className="text-center mb-6">
                <MapPin className="w-12 h-12 text-champagne mx-auto mb-3" />
                <h3 className="text-xl font-serif font-medium text-text-light mb-2">Perspective Map</h3>
                <p className="text-text-muted">
                  {mapData?.total_with_location || 0} moments have location data
                </p>
              </div>
              
              {!aura?.show_photo_map ? (
                <div className="text-center py-8 max-w-md mx-auto" data-testid="photo-map-off">
                  <p className="text-text-muted text-sm">
                    The photo map is off. When you turn it on, you (and only you) can see where photos with
                    location data were taken. Guests never see locations, and location data is removed from
                    every photo file anyone can open or download.
                  </p>
                  {aura?.is_host && (
                    <button
                      onClick={async () => { await savePrivacyToggle('show_photo_map', true); fetchMapData(); }}
                      disabled={privacyBusy}
                      className="btn-champagne text-sm mt-4"
                    >
                      Turn on the photo map
                    </button>
                  )}
                </div>
              ) : mapData && mapData.total_with_location > 0 ? (
                <div>
                  {/* Map placeholder - shows location pins */}
                  <div className="bg-midnight-light rounded-xl p-4 mb-6 min-h-[300px] relative overflow-hidden">
                    <div className="absolute inset-0 opacity-20" style={{
                      backgroundImage: 'radial-gradient(circle at 2px 2px, #D4AF37 1px, transparent 0)',
                      backgroundSize: '40px 40px'
                    }} />
                    
                    {/* Location markers */}
                    <div className="relative h-[300px]">
                      {mapData.locations.slice(0, 20).map((loc, idx) => {
                        // Normalize coordinates to fit in container
                        const x = ((loc.longitude - (mapData.center?.longitude || 0)) * 1000 + 50) % 90 + 5;
                        const y = ((loc.latitude - (mapData.center?.latitude || 0)) * -1000 + 50) % 90 + 5;
                        return (
                          <div
                            key={loc.id}
                            className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer group"
                            style={{ left: `${x}%`, top: `${y}%` }}
                            onClick={() => setSelectedMedia(loc)}
                          >
                            <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-champagne shadow-lg group-hover:scale-125 transition-transform">
                              <img
                                src={`${API_URL}${loc.url}`}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-champagne rotate-45" />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Location list */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {mapData.locations.map((loc) => (
                      <div
                        key={loc.id}
                        className="aura-card rounded-lg p-2 cursor-pointer hover:border-champagne transition-colors"
                        onClick={() => setSelectedMedia(loc)}
                      >
                        <img
                          src={`${API_URL}${loc.url}`}
                          alt={`Moment from ${loc.guest_name || 'a guest'}`}
                          className="w-full aspect-square object-cover rounded-md mb-2"
                        />
                        <p className="text-xs text-text-muted truncate">{loc.guest_name}</p>
                        <p className="text-xs text-champagne/70 inline-flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {loc.latitude?.toFixed(4)}, {loc.longitude?.toFixed(4)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-text-muted">No location data available</p>
                  <p className="text-sm text-text-muted mt-2">
                    Photos taken with GPS enabled will appear on the map
                  </p>
                </div>
              )}
            </div>
          ) : viewMode === 'clusters' && clusters ? (
            /* Cluster View */
            clusters.clusters.length === 0 ? (
              <EmptyState icon={Users} title="No people groups yet" message="Once guests add face photos, we'll group moments by who's in them." />
            ) : (
            <div className="space-y-6">
              {clusters.clusters.map((cluster) => (
                <motion.div
                  key={cluster.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="aura-card rounded-xl overflow-hidden"
                >
                  <button
                    onClick={() => setExpandedCluster(expandedCluster === cluster.id ? null : cluster.id)}
                    className="w-full p-4 flex items-center justify-between hover:bg-champagne/5 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {cluster.mood && <MoodIcon mood={cluster.mood} className="w-6 h-6 text-champagne" />}
                      <div className="text-left">
                        <h3 className="text-lg font-serif font-medium text-text-light">
                          {cluster.name}
                        </h3>
                        <p className="text-sm text-text-muted">
                          {cluster.count} moments
                          {cluster.type === 'time' && cluster.start_time && (
                            <span className="ml-2">
                              • {new Date(cluster.start_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    {expandedCluster === cluster.id ? (
                      <ChevronUp className="w-5 h-5 text-champagne" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-text-muted" />
                    )}
                  </button>
                  
                  <AnimatePresence>
                    {expandedCluster === cluster.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="border-t border-champagne/10"
                      >
                        <div className="p-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                          {cluster.media?.map((item) => (
                            <div
                              key={item.id}
                              className="aspect-square rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-champagne transition-all"
                              onClick={() => setSelectedMedia(item)}
                            >
                              {item.file_type === 'video' ? (
                                <video
                                  src={`${API_URL}${item.url}`}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <img
                                  src={`${API_URL}${item.url}`}
                                  alt={item.caption || `Moment from ${item.guest_name || 'a guest'}`}
                                  className="w-full h-full object-cover"
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
            )
          ) : viewMode === 'analytics' && analyticsData ? (
            /* Analytics View */
            <div className="space-y-6" data-testid="analytics-view">
              {/* Download Report + Highlight Reel */}
              <div className="flex flex-wrap justify-end gap-2">
                <button
                  onClick={openHighlightModal}
                  className="btn-outline flex items-center gap-2 text-sm"
                  data-testid="generate-highlight-btn"
                >
                  <Film className="w-4 h-4" />
                  Highlight Reel
                </button>
                <button
                  onClick={handleGenerateVoiceReel}
                  className="btn-outline flex items-center gap-2 text-sm"
                  data-testid="voice-toast-reel-btn"
                >
                  <Mic className="w-4 h-4" />
                  {voiceReel?.status === 'processing' || voiceReel?.status === 'queued' ? 'Rendering...' :
                   voiceReel?.status === 'ready' ? 'Voice reel ready' : 'Download voice-toast reel'}
                </button>
                <button
                  onClick={handleDownloadReport}
                  disabled={downloadingReport}
                  className="btn-outline flex items-center gap-2 text-sm"
                  data-testid="download-report-btn"
                >
                  <FileText className="w-4 h-4" />
                  {downloadingReport ? 'Generating...' : 'Download PDF Report'}
                </button>
                <button
                  onClick={handleDownloadPostcards}
                  disabled={downloadingPostcards}
                  className="btn-outline flex items-center gap-2 text-sm"
                  data-testid="download-postcards-btn"
                >
                  <Image className="w-4 h-4" />
                  {downloadingPostcards ? 'Generating...' : 'Download postcards (PDF)'}
                </button>
              </div>
              {voiceReelError && <p className="text-red-400 text-sm">{voiceReelError}</p>}
              {voiceReel?.status === 'ready' && voiceReel.url && (
                <div className="aura-card rounded-xl p-4 space-y-2">
                  <video src={`${API_URL}${voiceReel.url}`} controls className="w-full rounded-lg" />
                  <a href={`${API_URL}${voiceReel.url}`} download className="btn-outline inline-flex items-center gap-2 text-sm">
                    <Download className="w-4 h-4" /> Download MP4
                  </a>
                </div>
              )}
              {/* Summary Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="aura-card rounded-xl p-4">
                  <div className="flex items-center gap-2 text-text-muted text-sm mb-1">
                    <Image className="w-4 h-4" /> Total Media
                  </div>
                  <div className="text-2xl font-bold text-text-light">{analyticsData.total_media}</div>
                  <div className="text-xs text-text-muted mt-1">
                    {analyticsData.total_approved} approved &middot; {analyticsData.total_pending} pending
                  </div>
                </div>
                <div className="aura-card rounded-xl p-4">
                  <div className="flex items-center gap-2 text-text-muted text-sm mb-1">
                    <Heart className="w-4 h-4" /> Engagement
                  </div>
                  <div className="text-2xl font-bold text-champagne">{analyticsData.engagement.total_hearts + analyticsData.engagement.total_glows}</div>
                  <div className="text-xs text-text-muted mt-1">
                    {analyticsData.engagement.total_hearts} hearts &middot; {analyticsData.engagement.total_glows} glows
                  </div>
                </div>
                <div className="aura-card rounded-xl p-4">
                  <div className="flex items-center gap-2 text-text-muted text-sm mb-1">
                    <Users className="w-4 h-4" /> Contributors
                  </div>
                  <div className="text-2xl font-bold text-text-light">{analyticsData.top_contributors.length}</div>
                  <div className="text-xs text-text-muted mt-1">
                    Avg {analyticsData.engagement.avg_reactions} reactions/media
                  </div>
                </div>
                <div className="aura-card rounded-xl p-4">
                  <div className="flex items-center gap-2 text-text-muted text-sm mb-1">
                    <MapPin className="w-4 h-4" /> With Location
                  </div>
                  <div className="text-2xl font-bold text-text-light">{analyticsData.with_location}</div>
                  <div className="text-xs text-text-muted mt-1">GPS-tagged uploads</div>
                </div>
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Upload Timeline */}
                <div className="aura-card rounded-xl p-5">
                  <h3 className="text-text-light font-semibold mb-4">Upload Timeline</h3>
                  <div className="h-48">
                    {analyticsData.timeline.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={analyticsData.timeline}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                          <XAxis dataKey="date" tick={{ fill: '#9CA3AF', fontSize: 10 }} tickLine={false} />
                          <YAxis tick={{ fill: '#9CA3AF', fontSize: 10 }} tickLine={false} />
                          <Tooltip
                            contentStyle={{ background: '#252642', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                            labelStyle={{ color: '#D4AF37' }}
                            itemStyle={{ color: '#E5E7EB' }}
                          />
                          <Bar dataKey="uploads" fill="#D4AF37" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-full text-text-muted text-sm">No data</div>
                    )}
                  </div>
                </div>

                {/* Mood Distribution */}
                <div className="aura-card rounded-xl p-5">
                  <h3 className="text-text-light font-semibold mb-4">Mood Distribution</h3>
                  <div className="flex items-center gap-4">
                    <div className="w-36 h-36 flex-shrink-0">
                      {Object.keys(analyticsData.mood_distribution).length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={Object.entries(analyticsData.mood_distribution).map(([name, value]) => ({ name, value }))}
                              cx="50%" cy="50%" innerRadius={25} outerRadius={55} dataKey="value" stroke="none"
                            >
                              {Object.keys(analyticsData.mood_distribution).map((_, idx) => (
                                <Cell key={idx} fill={['#D4AF37', '#8E44AD', '#F39C12', '#10B981', '#EF4444', '#3B82F6'][idx % 6]} />
                              ))}
                            </Pie>
                          </PieChart>
                        </ResponsiveContainer>
                      ) : <div className="text-text-muted text-sm text-center mt-14">No data</div>}
                    </div>
                    <div className="flex-1 space-y-2">
                      {Object.entries(analyticsData.mood_distribution).map(([mood, count], idx) => (
                        <div key={mood} className="flex items-center gap-2 text-sm">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: ['#D4AF37', '#8E44AD', '#F39C12', '#10B981', '#EF4444', '#3B82F6'][idx % 6] }} />
                          <span className="text-text-muted capitalize flex-1">{mood}</span>
                          <span className="text-text-light font-medium">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Peak Hours */}
              {analyticsData.peak_hours.length > 0 && (
                <div className="aura-card rounded-xl p-5">
                  <h3 className="text-text-light font-semibold mb-4">Peak Upload Hours</h3>
                  <div className="h-40">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={analyticsData.peak_hours}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                        <XAxis
                          dataKey="hour"
                          tick={{ fill: '#9CA3AF', fontSize: 10 }}
                          tickFormatter={h => `${h}:00`}
                        />
                        <YAxis tick={{ fill: '#9CA3AF', fontSize: 10 }} tickLine={false} />
                        <Tooltip
                          contentStyle={{ background: '#252642', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                          labelFormatter={h => `${h}:00`}
                          labelStyle={{ color: '#D4AF37' }}
                          itemStyle={{ color: '#E5E7EB' }}
                        />
                        <Bar dataKey="count" fill="#8E44AD" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Top Contributors */}
              {analyticsData.top_contributors.length > 0 && (
                <div className="aura-card rounded-xl p-5">
                  <h3 className="text-text-light font-semibold mb-4">Top Contributors</h3>
                  <div className="space-y-2">
                    {analyticsData.top_contributors.map((c, idx) => (
                      <div key={idx} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-aura-purple to-aura-gold flex items-center justify-center text-white text-xs font-bold">
                          {idx + 1}
                        </div>
                        <div className="flex-1">
                          <div className="text-text-light text-sm font-medium">{c.name}</div>
                          <div className="text-text-muted text-xs">{c.count} uploads &middot; {c.hearts} hearts &middot; {c.glows} glows</div>
                        </div>
                        <div className="w-24 h-2 bg-white/5 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-champagne rounded-full"
                            style={{ width: `${(c.count / (analyticsData.top_contributors[0]?.count || 1)) * 100}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* File Type Breakdown */}
              <div className="aura-card rounded-xl p-5">
                <h3 className="text-text-light font-semibold mb-4">File Types</h3>
                <div className="flex gap-4">
                  {Object.entries(analyticsData.file_type_breakdown).map(([type, count]) => (
                    <div key={type} className="flex-1 text-center py-3 rounded-lg bg-white/5">
                      <div className="text-lg font-bold text-text-light">{count}</div>
                      <div className="text-xs text-text-muted capitalize">{type}s</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            /* Gallery View */
            <div className="masonry-grid">
              {media.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`masonry-item aura-card rounded-xl overflow-hidden cursor-pointer ${
                    index % 5 === 0 ? 'tall' : ''
                  }`}
                  onClick={() => setSelectedMedia(item)}
                  data-testid={`media-item-${item.id}`}
                >
                  {item.file_type === 'video' ? (
                    <video
                      src={`${API_URL}${item.url}`}
                      className="w-full h-full object-cover"
                    />
                  ) : item.file_type === 'audio' ? (
                    <div className="w-full aspect-square flex flex-col items-center justify-center bg-gradient-to-br from-aura-purple/20 to-aura-gold/20 p-4">
                      <Mic className="w-10 h-10 mb-2 text-champagne" />
                      <p className="text-sm text-text-muted">Voice Toast</p>
                    </div>
                  ) : item.file_type === 'text' ? (
                    <div className="w-full aspect-square flex flex-col items-center justify-center bg-gradient-to-br from-aura-purple/20 to-aura-gold/10 p-5">
                      <MessageSquare className="w-8 h-8 mb-3 text-champagne" />
                      <blockquote className="text-text-light text-sm italic text-center leading-relaxed line-clamp-6">
                        "{item.text_content}"
                      </blockquote>
                    </div>
                  ) : (
                    <img
                      src={`${API_URL}${item.url}`}
                      alt={item.caption || 'Uploaded moment'}
                      className="w-full h-full object-cover"
                    />
                  )}
                  <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-midnight to-transparent">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-text-light">
                        {item.guest_name}
                      </span>
                      {item.mood && (
                        <MoodIcon mood={item.mood} className="w-5 h-5 text-champagne" />
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Media Modal */}
      <AnimatePresence>
        {selectedMedia && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedMedia(null)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="aura-card rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative">
                {selectedMedia.file_type === 'video' ? (
                  <video
                    src={`${API_URL}${selectedMedia.url}`}
                    controls
                    className="w-full max-h-[60vh] object-contain bg-black"
                  />
                ) : selectedMedia.file_type === 'audio' ? (
                  <div className="w-full flex flex-col items-center justify-center bg-gradient-to-br from-aura-purple/20 to-aura-gold/20 p-10">
                    <Mic className="w-12 h-12 mb-4 text-champagne" />
                    <p className="text-sm text-text-muted mb-3">Voice Toast</p>
                    <audio src={`${API_URL}${selectedMedia.url}`} controls className="w-full max-w-sm" />
                  </div>
                ) : selectedMedia.file_type === 'text' ? (
                  <div className="w-full flex flex-col items-center justify-center bg-gradient-to-br from-aura-purple/20 to-aura-gold/10 p-10 min-h-[40vh]">
                    <MessageSquare className="w-10 h-10 mb-4 text-champagne" />
                    <blockquote className="text-text-light text-lg italic text-center leading-relaxed max-w-lg">
                      "{selectedMedia.text_content}"
                    </blockquote>
                  </div>
                ) : (
                  <img
                    src={`${API_URL}${selectedMedia.url}`}
                    alt={selectedMedia.caption || 'Moment'}
                    className="w-full max-h-[60vh] object-contain bg-black"
                  />
                )}
                <button
                  onClick={() => setSelectedMedia(null)}
                  className="absolute top-4 right-4 w-10 h-10 bg-midnight/80 rounded-full flex items-center justify-center text-text-light hover:bg-midnight transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6">
                {editingMedia?.id === selectedMedia.id ? (
                  <div className="space-y-3">
                    <input
                      className="aura-input w-full text-sm"
                      value={editingMedia.guest_name || ''}
                      onChange={e => setEditingMedia({ ...editingMedia, guest_name: e.target.value })}
                      placeholder="Guest name"
                    />
                    <textarea
                      className="aura-input w-full text-sm resize-none"
                      rows={2}
                      value={editingMedia.caption || ''}
                      onChange={e => setEditingMedia({ ...editingMedia, caption: e.target.value })}
                      placeholder="Caption"
                    />
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setEditingMedia({ ...editingMedia, mood: '' })}
                        className={`px-3 py-1 rounded text-sm ${!editingMedia.mood ? 'bg-champagne text-midnight' : 'bg-white/5 text-text-light'}`}
                      >None</button>
                      {MOODS.map(m => (
                        <button key={m.value} type="button"
                          onClick={() => setEditingMedia({ ...editingMedia, mood: m.value })}
                          className={`px-3 py-1 rounded text-sm inline-flex items-center gap-1 ${editingMedia.mood === m.value ? 'bg-champagne text-midnight' : 'bg-white/5 text-text-light'}`}>
                          <m.Icon className="w-3.5 h-3.5" /> {m.label}
                        </button>
                      ))}
                    </div>
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => setEditingMedia(null)} className="btn-outline text-sm px-4 py-2">Cancel</button>
                      <button onClick={saveMediaEdit} className="btn-champagne text-sm px-4 py-2">Save</button>
                    </div>
                  </div>
                ) : (
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-text-light">
                        {selectedMedia.guest_name}
                      </p>
                      {selectedMedia.mood && (
                        <span className="mood-badge text-xs inline-flex items-center gap-1">
                          <MoodIcon mood={selectedMedia.mood} className="w-3.5 h-3.5" /> {selectedMedia.mood}
                        </span>
                      )}
                    </div>
                    {selectedMedia.caption && (
                      <p className="text-text-muted">{selectedMedia.caption}</p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-sm text-text-muted">
                      <span className="inline-flex items-center gap-1"><Heart className="w-4 h-4" /> {selectedMedia.hearts}</span>
                      <span className="inline-flex items-center gap-1"><Sparkles className="w-4 h-4" /> {selectedMedia.glows}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {selectedMedia.file_type !== 'text' && (
                      <a
                        href={`${API_URL}${selectedMedia.url}`}
                        download
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-text-muted hover:text-champagne hover:bg-champagne/10 rounded-lg transition-colors"
                        data-testid="download-single-btn"
                      >
                        <Download className="w-5 h-5" />
                      </a>
                    )}
                    <button
                      onClick={() => setEditingMedia({ id: selectedMedia.id, caption: selectedMedia.caption || '', mood: selectedMedia.mood || '', guest_name: selectedMedia.guest_name || '' })}
                      className="p-2 text-text-muted hover:text-champagne hover:bg-champagne/10 rounded-lg transition-colors"
                      data-testid="edit-media-btn"
                      aria-label="Edit moment"
                    >
                      <Pencil className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDeleteMedia(selectedMedia.id)}
                      className="p-2 text-text-muted hover:text-error hover:bg-error/10 rounded-lg transition-colors"
                      data-testid="delete-media-btn"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Share Live View Modal */}
      <AnimatePresence>
        {showLiveModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowLiveModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="aura-card rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
              data-testid="live-share-modal"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-serif font-medium text-text-light flex items-center gap-2">
                  <Radio className="w-5 h-5 text-champagne" /> Share Live View
                </h3>
                <button onClick={() => setShowLiveModal(false)} className="p-2.5 -mr-2.5 text-text-muted hover:text-text-light" aria-label="Close">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-text-muted mb-5">
                Send this to people who can’t be there. They’ll see every photo, video and note as guests share it, in real time.
                It’s view-only: they can’t upload, and they don’t need the portal password.
              </p>

              {!liveShare ? (
                <p className="text-text-muted text-sm py-6 text-center animate-pulse">Loading…</p>
              ) : !liveShare.enabled ? (
                <div className="text-center py-4">
                  <button onClick={createLiveShare} disabled={liveBusy} className="btn-champagne px-6 py-3" data-testid="live-share-create">
                    {liveBusy ? 'Creating…' : 'Create live view link'}
                  </button>
                </div>
              ) : (
                <div className="space-y-5">
                  <div className="flex gap-2">
                    <input
                      readOnly
                      value={liveShare.url}
                      onFocus={(e) => e.target.select()}
                      className="aura-input text-sm flex-1 min-w-0"
                      data-testid="live-share-url"
                    />
                    <button onClick={copyLiveShare} className="btn-champagne px-4 flex items-center gap-2 shrink-0" data-testid="live-share-copy">
                      {liveCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      {liveCopied ? 'Copied' : 'Copy'}
                    </button>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <a
                      href={`https://wa.me/?text=${encodeURIComponent(`Watch ${aura.title} live as guests share their moments: ${liveShare.url}`)}`}
                      target="_blank" rel="noopener noreferrer"
                      className="btn-outline text-sm flex items-center justify-center gap-2 py-2.5"
                    >
                      <MessageCircle className="w-4 h-4" /> WhatsApp
                    </a>
                    <a
                      href={`mailto:?subject=${encodeURIComponent(`Watch ${aura.title} live`)}&body=${encodeURIComponent(`You can follow ${aura.title} live — photos appear as guests share them:\n\n${liveShare.url}`)}`}
                      className="btn-outline text-sm flex items-center justify-center gap-2 py-2.5"
                    >
                      <Mail className="w-4 h-4" /> Email
                    </a>
                    <a href={liveShare.url} target="_blank" rel="noopener noreferrer" className="btn-outline text-sm flex items-center justify-center gap-2 py-2.5 col-span-2 sm:col-span-1">
                      <ExternalLink className="w-4 h-4" /> Open
                    </a>
                  </div>

                  <div className="flex justify-center">
                    <div className="bg-white p-3 rounded-lg">
                      <QRCodeSVG value={liveShare.url} size={148} />
                    </div>
                  </div>

                  <div className="pt-4 border-t border-champagne/20 flex flex-col sm:flex-row gap-2">
                    <button onClick={createLiveShare} disabled={liveBusy} className="btn-outline text-sm flex-1 flex items-center justify-center gap-2 py-2.5" data-testid="live-share-regenerate">
                      <RefreshCw className="w-4 h-4" /> New link
                    </button>
                    <button onClick={disableLiveShare} disabled={liveBusy} className="btn-outline text-sm flex-1 py-2.5 hover:!border-red-400 hover:!text-red-400" data-testid="live-share-disable">
                      Turn off
                    </button>
                  </div>
                  <p className="text-xs text-text-muted">“New link” replaces this one if it went to the wrong person. “Turn off” stops sharing and disconnects anyone watching.</p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Settings Modal */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowSettings(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="aura-card rounded-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-serif font-medium text-text-light mb-6">Aura Settings</h3>

              {/* Event details */}
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-text-light mb-1.5">Event name</label>
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    className="aura-input"
                    data-testid="edit-title-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-light mb-1.5">Description</label>
                  <textarea
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    className="aura-input resize-none"
                    rows={2}
                    data-testid="edit-description-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-light mb-1.5">Event date</label>
                  <input
                    type="date"
                    value={editDate}
                    onChange={(e) => setEditDate(e.target.value)}
                    className="aura-input cursor-pointer [color-scheme:dark]"
                    data-testid="edit-date-input"
                  />
                  <label className="flex items-center gap-2 mt-2 text-sm text-text-muted cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={editMultiDay}
                      onChange={(e) => { setEditMultiDay(e.target.checked); if (!e.target.checked) setEditEndDate(''); }}
                      className="accent-champagne"
                      data-testid="edit-multiday-toggle"
                    />
                    Multi-day event (e.g. a vacation)
                  </label>
                  {editMultiDay && (
                    <input
                      type="date"
                      value={editEndDate}
                      onChange={(e) => setEditEndDate(e.target.value)}
                      min={editDate}
                      className="aura-input cursor-pointer [color-scheme:dark] mt-2"
                      data-testid="edit-end-date-input"
                    />
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-text-light mb-1.5">Occasion</label>
                    <select
                      value={editOccasion}
                      onChange={(e) => setEditOccasion(e.target.value)}
                      className="aura-input cursor-pointer [color-scheme:dark]"
                      data-testid="edit-occasion-select"
                    >
                      {OCCASIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-light mb-1.5">Vibe</label>
                    <select
                      value={editVibe}
                      onChange={(e) => setEditVibe(e.target.value)}
                      className="aura-input cursor-pointer [color-scheme:dark]"
                      data-testid="edit-vibe-select"
                    >
                      {VIBES.map((v) => (
                        <option key={v.value} value={v.value}>{v.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {editOccasion === 'vacation' && (
                  <p className="text-xs text-text-muted">Vacations don’t use RSVP or seating, so those tools stay hidden.</p>
                )}
              </div>

              <label className="block text-sm font-medium text-text-light mb-3 pt-4 border-t border-champagne/20">Privacy</label>
              <div className="space-y-3 mb-6">
                {[
                  { value: 'open',      Icon: Globe, label: 'Open',      desc: 'Anyone can view & upload' },
                  { value: 'locked',    Icon: Lock,  label: 'Locked',    desc: 'Password required' },
                  { value: 'moderated', Icon: Eye,   label: 'Moderated', desc: 'You approve uploads' }
                ].map((mode) => (
                  <button
                    key={mode.value}
                    onClick={() => setPrivacyMode(mode.value)}
                    className={`w-full p-4 rounded-xl border transition-all flex items-center gap-4 text-left ${
                      privacyMode === mode.value
                        ? 'border-champagne bg-champagne/10'
                        : 'border-champagne/20 hover:border-champagne/40'
                    }`}
                  >
                    <mode.Icon className={`w-5 h-5 shrink-0 ${privacyMode === mode.value ? 'text-champagne' : 'text-text-muted'}`} />
                    <div>
                      <p className={`font-medium ${privacyMode === mode.value ? 'text-champagne' : 'text-text-light'}`}>
                        {mode.label}
                      </p>
                      <p className="text-sm text-text-muted">{mode.desc}</p>
                    </div>
                  </button>
                ))}
              </div>

              {privacyMode === 'locked' && (
                <div className="mb-6 -mt-2">
                  <label htmlFor="edit-portal-password" className="block text-sm font-medium text-text-light mb-1.5">
                    {aura?.has_password ? 'Change portal password' : 'Portal password *'}
                  </label>
                  <div className="relative">
                    <input
                      id="edit-portal-password"
                      type={showEditPassword ? 'text' : 'password'}
                      value={editPassword}
                      onChange={(e) => setEditPassword(e.target.value)}
                      className="aura-input pr-12"
                      placeholder={aura?.has_password ? 'Leave blank to keep the current password' : 'Enter password for guests'}
                      autoComplete="new-password"
                      data-testid="edit-portal-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowEditPassword((v) => !v)}
                      className="absolute right-1 top-1/2 -translate-y-1/2 p-2.5 text-text-muted hover:text-champagne min-w-[44px] min-h-[44px] flex items-center justify-center"
                      aria-label={showEditPassword ? 'Hide password' : 'Show password'}
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-xs text-text-muted mt-1.5">
                    {aura?.has_password
                      ? 'Forgot it? Passwords are stored securely and can’t be shown — just set a new one. Guests already inside will be asked for the new password.'
                      : 'Guests will need this password to open the portal.'}
                  </p>
                </div>
              )}

              {/* Photo privacy (host only) */}
              {aura?.is_host && (
                <div className="mt-6 pt-6 border-t border-champagne/20" data-testid="photo-privacy-settings">
                  <h4 className="text-sm font-medium text-text-light mb-3 flex items-center gap-2">
                    <Lock className="w-4 h-4 text-champagne" /> Photo privacy
                  </h4>
                  <label className="flex items-start gap-3 mb-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!aura?.show_photo_map}
                      disabled={privacyBusy}
                      onChange={(e) => savePrivacyToggle('show_photo_map', e.target.checked)}
                      className="mt-1 w-4 h-4 accent-champagne shrink-0"
                      data-testid="toggle-photo-map"
                    />
                    <span className="text-xs text-text-muted">
                      <span className="text-text-light text-sm block">Photo map (only you)</span>
                      Show where photos were taken on the perspective map. Off by default. Guests never see locations.
                    </span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!aura?.face_recognition_enabled}
                      disabled={privacyBusy}
                      onChange={(e) => savePrivacyToggle('face_recognition_enabled', e.target.checked)}
                      className="mt-1 w-4 h-4 accent-champagne shrink-0"
                      data-testid="toggle-find-me"
                    />
                    <span className="text-xs text-text-muted">
                      <span className="text-text-light text-sm block">Find me (selfie search)</span>
                      Lets guests who agree search the photos for their own face with a selfie. The portal tells guests
                      it is on. Faces are only detected while someone searches; no face data is stored. Business plan or higher.
                    </span>
                  </label>
                </div>
              )}

              {/* Co-hosts Section */}
              <div className="mt-6 pt-6 border-t border-champagne/20">
                <h4 className="text-sm font-medium text-text-light mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4 text-champagne" /> Co-hosts
                </h4>
                {collaborators.length > 0 ? (
                  <div className="space-y-2 mb-3">
                    {collaborators.map(c => (
                      <div key={c.id} className="flex items-center justify-between bg-midnight/50 rounded-lg p-2 px-3">
                        <div>
                          <p className="text-sm text-text-light">{c.name}</p>
                          <p className="text-xs text-text-muted">{c.email}</p>
                        </div>
                        <button
                          onClick={() => handleRemoveCoHost(c.id)}
                          className="p-2.5 text-text-muted hover:text-red-400 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-text-muted mb-3">No co-hosts yet. Business plan required.</p>
                )}
                <div className="flex gap-2">
                  <input
                    type="email"
                    placeholder="co-host@email.com"
                    value={newCoHostEmail}
                    onChange={e => setNewCoHostEmail(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleAddCoHost()}
                    className="aura-input flex-1 text-sm py-2"
                  />
                  <button
                    onClick={handleAddCoHost}
                    disabled={collaboratorLoading || !newCoHostEmail.trim()}
                    className="btn-champagne px-3 py-2 text-sm"
                  >
                    <UserPlus className="w-4 h-4" />
                  </button>
                </div>
                {collaboratorError && (
                  <p className="text-xs text-red-400 mt-2">{collaboratorError}</p>
                )}
              </div>

              {/* Kiosk Mode Section */}
              <div className="mt-6 pt-6 border-t border-champagne/20">
                <h4 className="text-sm font-medium text-text-light mb-1 flex items-center gap-2">
                  <Monitor className="w-4 h-4 text-champagne" /> Kiosk mode
                </h4>
                <p className="text-xs text-text-muted mb-3">
                  A full-screen, upload-only page for a shared device at your event. Set a 4-digit PIN to lock the exit.
                </p>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={4}
                    placeholder="4-digit PIN"
                    value={kioskPin}
                    onChange={e => setKioskPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    className="aura-input flex-1 text-sm py-2"
                    data-testid="kiosk-pin-input"
                  />
                  <button onClick={() => saveKioskPin(false)} disabled={kioskBusy}
                    className="btn-champagne px-3 py-2 text-sm">Set PIN</button>
                  <button onClick={() => saveKioskPin(true)} disabled={kioskBusy}
                    className="btn-outline px-3 py-2 text-sm">Remove</button>
                </div>
                <div className="flex items-center gap-2 bg-midnight/50 rounded-lg p-2 px-3">
                  <span className="text-xs text-text-muted truncate flex-1">{kioskUrl}</span>
                  <button
                    onClick={() => { navigator.clipboard?.writeText(kioskUrl); toast.success('Kiosk link copied'); }}
                    className="p-2 text-text-muted hover:text-champagne shrink-0" aria-label="Copy kiosk link"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {settingsError && (
                <p className="text-sm text-red-400 mt-4" data-testid="settings-error">{settingsError}</p>
              )}
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowSettings(false)}
                  className="flex-1 btn-outline"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateSettings}
                  disabled={savingSettings}
                  className="flex-1 btn-champagne"
                  data-testid="save-settings-btn"
                >
                  {savingSettings ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Aura Card PDF Modal */}
      <AnimatePresence>
        {showCardModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowCardModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="aura-card rounded-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-serif font-medium text-text-light mb-2">Generate Aura Card</h3>
              <p className="text-text-muted mb-6">Create a printable PDF card with your QR code</p>
              
              {/* Live preview — reflects chosen colors + text */}
              <div className="rounded-xl p-5 mb-5 text-center border" style={{ backgroundColor: cardPrimary, borderColor: cardAccent }}>
                <p className="text-[10px] uppercase tracking-[0.3em] mb-2" style={{ color: cardAccent }}>MEMOURAA</p>
                <p className="font-serif text-lg mb-1" style={{ color: '#FAFAFA' }}>{cardHeadline || aura.title}</p>
                {cardShowDate && <p className="text-xs mb-3" style={{ color: '#D1D5DB' }}>{formatDate()}</p>}
                <div className="bg-white p-2 rounded-lg inline-block mb-3">
                  <QRCodeSVG value={portalUrl} size={72} fgColor="#1A1B2E" level="M" />
                </div>
                <p className="text-sm" style={{ color: '#FAFAFA' }}>{cardMessage}</p>
                {cardSubline && <p className="text-xs mt-1" style={{ color: '#D1D5DB' }}>{cardSubline}</p>}
                {cardPassword && <p className="text-sm font-semibold mt-1" style={{ color: cardAccent }}>Password: {cardPassword}</p>}
              </div>

              {/* Text fields */}
              <div className="space-y-3 mb-5">
                <div>
                  <label className="block text-xs font-medium text-text-light mb-1">Headline <span className="text-text-muted">(optional — overrides the title)</span></label>
                  <input type="text" value={cardHeadline} onChange={(e) => setCardHeadline(e.target.value)} className="aura-input" placeholder={aura.title} data-testid="card-headline-input" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-light mb-1">Message</label>
                  <input type="text" value={cardMessage} onChange={(e) => setCardMessage(e.target.value)} className="aura-input" placeholder="Scan to share your moments" data-testid="card-message-input" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-light mb-1">Sub-line <span className="text-text-muted">(optional)</span></label>
                  <input type="text" value={cardSubline} onChange={(e) => setCardSubline(e.target.value)} className="aura-input" placeholder="e.g. We can't wait to celebrate with you" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-text-light mb-1">
                    Event password <span className="text-text-muted">(optional — printed on the card{aura.privacy_mode === 'locked' ? '; your event is password-protected' : ''})</span>
                  </label>
                  <input type="text" value={cardPassword} onChange={(e) => setCardPassword(e.target.value)} className="aura-input" placeholder="Type the password to print it for guests" data-testid="card-password-input" />
                </div>
              </div>

              {/* Colors */}
              <div className="flex gap-5 mb-5">
                <label className="flex items-center gap-2 text-sm text-text-light">
                  <input type="color" value={cardPrimary} onChange={(e) => setCardPrimary(e.target.value)} className="w-9 h-9 rounded cursor-pointer bg-transparent border border-white/20" />
                  Background
                </label>
                <label className="flex items-center gap-2 text-sm text-text-light">
                  <input type="color" value={cardAccent} onChange={(e) => setCardAccent(e.target.value)} className="w-9 h-9 rounded cursor-pointer bg-transparent border border-white/20" />
                  Accent
                </label>
              </div>

              {/* Size */}
              <div className="mb-4">
                <label className="block text-xs font-medium text-text-light mb-2">Size</label>
                <div className="flex flex-wrap gap-2">
                  {[['a4', 'A4'], ['letter', 'US Letter'], ['5x7', '5×7 card'], ['tent', 'Folded tent']].map(([val, lbl]) => (
                    <button key={val} type="button" onClick={() => setCardSize(val)}
                      className={`px-3 py-1.5 rounded text-xs border ${cardSize === val ? 'bg-champagne text-midnight border-champagne' : 'border-white/20 text-text-light'}`}>
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>

              {/* Style */}
              <div className="mb-4">
                <label className="block text-xs font-medium text-text-light mb-2">Style</label>
                <div className="flex flex-wrap gap-2">
                  {[['minimal', 'Minimal'], ['ornate', 'Ornate'], ['photo', 'Photo']].map(([val, lbl]) => (
                    <button key={val} type="button" onClick={() => setCardStyleName(val)}
                      className={`px-3 py-1.5 rounded text-xs border ${cardStyleName === val ? 'bg-champagne text-midnight border-champagne' : 'border-white/20 text-text-light'}`}>
                      {lbl}
                    </button>
                  ))}
                </div>
                {cardStyleName === 'photo' && !aura.cover_image && (
                  <p className="text-[11px] text-text-muted mt-1">No cover image set — Photo style falls back to the background color.</p>
                )}
              </div>

              {/* Cover toggle */}
              {aura.cover_image && (
                <label className="flex items-center gap-2 text-sm text-text-light mb-4">
                  <input type="checkbox" checked={cardUseCover} onChange={(e) => setCardUseCover(e.target.checked)} className="accent-champagne" />
                  Use the cover photo as the background
                </label>
              )}

              {/* Show/hide toggles */}
              <div className="grid grid-cols-2 gap-2 mb-6">
                {[['Date', cardShowDate, setCardShowDate], ['Vibe', cardShowVibe, setCardShowVibe], ['Short URL', cardShowUrl, setCardShowUrl], ['Instructions', cardShowInstructions, setCardShowInstructions]].map(([lbl, val, setter]) => (
                  <label key={lbl} className="flex items-center gap-2 text-sm text-text-light">
                    <input type="checkbox" checked={val} onChange={(e) => setter(e.target.checked)} className="accent-champagne" />
                    Show {lbl}
                  </label>
                ))}
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowCardModal(false)}
                  className="flex-1 btn-outline"
                >
                  Cancel
                </button>
                <button
                  onClick={handleGenerateCard}
                  disabled={generatingCard}
                  className="flex-1 btn-champagne disabled:opacity-50"
                  data-testid="generate-card-btn"
                >
                  {generatingCard ? 'Generating...' : 'Download PDF'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Share Modal */}
      <AnimatePresence>
        {showShareModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => {
              setShowShareModal(false);
              setShareMediaItem(null);
            }}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="aura-card rounded-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-serif font-medium text-text-light mb-2">Share Your Aura</h3>
              <p className="text-text-muted mb-6">
                {shareMediaItem ? 'Share this moment' : 'Invite guests to contribute'}
              </p>
              
              <div className="grid grid-cols-4 gap-3 mb-6">
                {SHARE_PLATFORMS.map((platform) => (
                  <button
                    key={platform.id}
                    onClick={() => handleShare(platform.id, shareMediaItem)}
                    className="flex flex-col items-center gap-2 p-3 rounded-xl border border-champagne/20 hover:border-champagne hover:bg-champagne/10 transition-all"
                    data-testid={`share-${platform.id}`}
                  >
                    <platform.Icon className="w-7 h-7" style={{ color: platform.color }} />
                    <span className="text-xs text-text-muted">{platform.name}</span>
                  </button>
                ))}
              </div>

              <div className="text-center">
                <button
                  onClick={() => {
                    setShowShareModal(false);
                    setShareMediaItem(null);
                  }}
                  className="text-text-muted hover:text-text-light transition-colors"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Invite Guests Modal */}
      <AnimatePresence>
        {showInviteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowInviteModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="aura-card rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-serif font-medium text-text-light flex items-center gap-2">
                  <Mail className="w-5 h-5 text-champagne" /> Invite Guests
                </h3>
                <button onClick={() => setShowInviteModal(false)} className="text-text-muted hover:text-text-light">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {inviteResult && !inviteResult.error ? (
                <div className="text-center py-6">
                  <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
                  <p className="text-text-light font-medium">Invitations Sent!</p>
                  <p className="text-text-muted text-sm mt-1">
                    {inviteResult.sent} sent{inviteResult.failed > 0 ? `, ${inviteResult.failed} failed` : ''}
                  </p>
                  <button onClick={() => setInviteResult(null)} className="btn-outline mt-4 text-sm">
                    Send More
                  </button>
                </div>
              ) : (
                <>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm text-text-muted block mb-2">Guest email addresses</label>
                      <textarea
                        value={inviteEmails}
                        onChange={e => setInviteEmails(e.target.value)}
                        placeholder="alice@example.com, bob@example.com&#10;One per line or comma-separated (max 50)"
                        rows={4}
                        className="aura-input w-full text-sm resize-none"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-text-muted block mb-2">Personal message (optional)</label>
                      <textarea
                        value={inviteMessage}
                        onChange={e => setInviteMessage(e.target.value)}
                        placeholder="Join us and upload your favourite memories!"
                        rows={2}
                        className="aura-input w-full text-sm resize-none"
                      />
                    </div>
                    {inviteResult?.error && (
                      <p className="text-red-400 text-sm">{inviteResult.error}</p>
                    )}
                  </div>
                  <div className="flex gap-3 mt-6">
                    <button onClick={() => setShowInviteModal(false)} className="flex-1 btn-outline">
                      Cancel
                    </button>
                    <button
                      onClick={handleSendInvites}
                      disabled={inviteSending || !inviteEmails.trim()}
                      className="flex-1 btn-champagne flex items-center justify-center gap-2"
                    >
                      <Mail className="w-4 h-4" />
                      {inviteSending ? 'Sending...' : 'Send Invitations'}
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Highlight Reel Modal */}
      <AnimatePresence>
        {showHighlightModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowHighlightModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="aura-card rounded-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-serif font-medium text-text-light flex items-center gap-2">
                  <Film className="w-5 h-5 text-champagne" /> Highlight Reel
                </h3>
                <button onClick={() => setShowHighlightModal(false)} className="text-text-muted hover:text-text-light">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {!highlightReel ? (
                <>
                  <p className="text-text-muted text-sm mb-4">
                    Auto-generates a 60-second video from your top photos & videos with music.
                  </p>
                  <label className="text-sm text-text-muted block mb-2">Background music</label>
                  <select
                    value={selectedTrack}
                    onChange={(e) => setSelectedTrack(e.target.value)}
                    className="aura-input w-full mb-4"
                  >
                    {musicTracks.length === 0 && <option value="elegant_piano">Elegant Piano</option>}
                    {musicTracks.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.id.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} {t.mood ? `(${t.mood})` : ''}
                      </option>
                    ))}
                  </select>
                  {highlightError && <p className="text-red-400 text-sm mb-3">{highlightError}</p>}
                  <button onClick={handleGenerateHighlight} className="btn-champagne w-full flex items-center justify-center gap-2">
                    <Film className="w-4 h-4" /> Generate Reel
                  </button>
                </>
              ) : highlightReel.status === 'ready' ? (
                <div className="text-center">
                  <Check className="w-12 h-12 text-green-400 mx-auto mb-3" />
                  <p className="text-text-light font-medium mb-2">Your reel is ready!</p>
                  <video
                    src={`${API_URL}${highlightReel.output_path}`}
                    controls
                    className="w-full rounded-lg mb-4 max-h-64"
                  />
                  <a
                    href={`${API_URL}${highlightReel.output_path}`}
                    download
                    className="btn-champagne flex items-center justify-center gap-2"
                  >
                    <Download className="w-4 h-4" /> Download MP4
                  </a>
                </div>
              ) : highlightReel.status === 'failed' ? (
                <div className="text-center">
                  <X className="w-12 h-12 text-red-400 mx-auto mb-3" />
                  <p className="text-red-400 mb-2">Generation failed</p>
                  <p className="text-text-muted text-xs mb-4">{highlightReel.error || 'Try again later'}</p>
                  <button onClick={() => setHighlightReel(null)} className="btn-outline">Try Again</button>
                </div>
              ) : (
                <div className="text-center py-6">
                  <div className="w-16 h-16 rounded-full bg-champagne/10 flex items-center justify-center mx-auto mb-4 animate-pulse">
                    <Film className="w-8 h-8 text-champagne" />
                  </div>
                  <p className="text-text-light font-medium">Building your reel...</p>
                  <p className="text-text-muted text-sm mt-1">This usually takes under 90 seconds. Reload this page to refresh status.</p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {showReelEditor && (
        <ReelEditor
          auraId={auraId}
          photos={media}
          onClose={() => setShowReelEditor(false)}
          onUploaded={() => { setShowReelEditor(false); fetchAuraAndMedia(); }}
        />
      )}
      <FooterExpanded />
    </div>
  );
}
